import { useState } from "react";
import { Avatar, Box, Chip, Rating, Typography } from "@mui/material";
import {
  ThumbUp as ThumbUpIcon,
  ThumbUpOutlined as ThumbUpOutlinedIcon,
  StorefrontOutlined as StorefrontOutlinedIcon,
  Inventory2Outlined as Inventory2OutlinedIcon,
} from "@mui/icons-material";
import { generatePath, Link, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import ImageWithLoader from "../../common-components/ImageWithLoader";
import VerifiedBadge from "../../common-components/VerifiedBadge";
import { resolveImageUri } from "../../utility/constants";
import { frontendRoutes } from "../../utility/frontendRoutes";
import {
  buildShopPath,
  buildUserPath,
} from "../../utility/utilityFunctions";
import {
  likeReview,
  unlikeReview,
} from "../../utility/httpActions/reviewActions";
import useAuthentication from "../../hooks/useAuthentication";

const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
};

const LIKE_ACTIVE_COLOR = "#046287";
const LIKE_INACTIVE_COLOR = "#4B5563";

const LikeButton = ({ reviewId, likesCount, isLikedByUser }) => {
  const { isLoggedIn } = useAuthentication();
  const queryClient = useQueryClient();
  const [optimistic, setOptimistic] = useState({
    liked: isLikedByUser,
    count: likesCount,
  });

  const { mutate } = useMutation({
    mutationFn: (currentlyLiked) =>
      currentlyLiked ? unlikeReview(reviewId) : likeReview(reviewId),
    onMutate: (currentlyLiked) => {
      setOptimistic({
        liked: !currentlyLiked,
        count: currentlyLiked ? optimistic.count - 1 : optimistic.count + 1,
      });
    },
    onError: () => {
      setOptimistic({ liked: isLikedByUser, count: likesCount });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reviewFeed"] });
    },
  });

  const color = optimistic.liked ? LIKE_ACTIVE_COLOR : LIKE_INACTIVE_COLOR;

  return (
    <Box
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isLoggedIn) mutate(optimistic.liked);
      }}
      title={
        isLoggedIn ? (optimistic.liked ? "Unlike" : "Like") : "Sign in to like"
      }
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "5px",
        cursor: isLoggedIn ? "pointer" : "default",
        py: "2px",
        userSelect: "none",
      }}
    >
      {optimistic.liked ? (
        <ThumbUpIcon sx={{ fontSize: 19, color }} />
      ) : (
        <ThumbUpOutlinedIcon sx={{ fontSize: 19, color }} />
      )}
      {optimistic.count > 0 && (
        <Typography
          sx={{ fontSize: 13, color, fontWeight: 500, lineHeight: 1 }}
        >
          {optimistic.count}
        </Typography>
      )}
    </Box>
  );
};

const FeedReviewCard = ({ review }) => {
  const navigate = useNavigate();
  const featured = review._featuredProductRating;
  const isProductCentric = !!featured;
  const reviewer = review.reviewer;
  const shop = review.shop;

  const reviewerName = reviewer?.name?.split(" ")[0] || "Someone";
  const avatarSrc = reviewer?.profilePhoto
    ? resolveImageUri(reviewer.profilePhoto)
    : undefined;
  const mainRating = isProductCentric ? featured.rating : review.shopRating;
  const displayText = isProductCentric
    ? featured.text || review.shopText
    : review.shopText;
  const media =
    isProductCentric && featured.media?.length > 0
      ? featured.media
      : review.media;

  // featured.product is just a raw ObjectId (a snapshot on the review's
  // embedded productRatings entry, not a populated Product doc), so there's
  // no slug to prefer here — falls back to id, same as VIEW_PRODUCT's own
  // backend resolver would for any bare id.
  const cardHref =
    isProductCentric && featured.product
      ? generatePath(frontendRoutes.VIEW_PRODUCT, {
          productId: featured.product.toString(),
        })
      : shop
        ? buildShopPath(shop)
        : null;

  const reviewerHref = reviewer ? buildUserPath(reviewer) : null;
  const shopHref = shop ? buildShopPath(shop) : null;

  return (
    <Box
      onClick={() => cardHref && navigate(cardHref)}
      sx={{
        display: "block",
        color: "inherit",
        bgcolor: "white",
        borderRadius: 2,
        border: "2px solid #e5e7eb",
        overflow: "hidden",
        cursor: cardHref ? "pointer" : "default",
      }}
    >
      {/* Header: reviewer → "reviewed" → entity */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 1.5,
          pt: 1.5,
          pb: 1,
        }}
      >
        {/* Reviewer avatar */}
        <Box
          component={reviewerHref ? Link : "div"}
          to={reviewerHref || undefined}
          onClick={(e) => e.stopPropagation()}
          sx={{ textDecoration: "none", flexShrink: 0 }}
        >
          <Avatar
            src={avatarSrc}
            sx={{ width: 36, height: 36, bgcolor: "#046287", fontSize: 13 }}
          >
            {reviewerName[0]?.toUpperCase()}
          </Avatar>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            <Typography
              component={reviewerHref ? Link : "span"}
              to={reviewerHref || undefined}
              onClick={(e) => e.stopPropagation()}
              variant="body2"
              fontWeight={600}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: "#111827",
                textDecoration: "none",
                "&:hover": {
                  textDecoration: reviewerHref ? "underline" : "none",
                },
              }}
            >
              {reviewerName}
              {reviewer?.isIdentityVerified && <VerifiedBadge size={13} />}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              reviewed
            </Typography>
            {isProductCentric ? (
              <Typography
                variant="body2"
                fontWeight={600}
                color="#046287"
                noWrap
              >
                {featured.productName}
              </Typography>
            ) : (
              <Typography
                component={shopHref ? Link : "span"}
                to={shopHref || undefined}
                onClick={(e) => e.stopPropagation()}
                variant="body2"
                fontWeight={600}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: "#046287",
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: shopHref ? "underline" : "none",
                  },
                }}
              >
                {shop?.shopName || "a shop"}
                {shop?.isIdentityVerified && <VerifiedBadge size={13} />}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25 }}>
            <Rating
              value={mainRating || 0}
              precision={0.1}
              readOnly
              size="small"
              sx={{ fontSize: 13 }}
            />
            <Typography variant="caption" color="text.secondary">
              {timeAgo(review.createdAt)}
            </Typography>
          </Box>
        </Box>

        {/* Shop avatar (top-right) */}
        {shop?.profilePhoto && (
          <Box
            component={shopHref ? Link : "div"}
            to={shopHref || undefined}
            onClick={(e) => e.stopPropagation()}
            sx={{ textDecoration: "none", flexShrink: 0 }}
          >
            <ImageWithLoader
              src={resolveImageUri(shop.profilePhoto)}
              alt={shop.shopName || "Shop"}
              sx={{ width: 32, height: 32, borderRadius: 1.5 }}
            />
          </Box>
        )}
      </Box>

      {/* Review text */}
      {!!displayText && (
        <Typography
          variant="body2"
          color="text.primary"
          sx={{
            px: 1.5,
            pb: 1.5,
            lineHeight: 1.6,
            display: "-webkit-box",
            WebkitLineClamp: 4,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {displayText}
        </Typography>
      )}

      {/* Tags */}
      {review.tags?.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.75,
            px: 1.5,
            pb: 1.5,
          }}
        >
          {review.tags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              sx={{
                bgcolor: "#eff6ff",
                color: "#046287",
                border: "1px solid #bfdbfe",
                height: 22,
                fontSize: 11,
              }}
            />
          ))}
        </Box>
      )}

      {/* Media thumbnails */}
      {media?.length > 0 && (
        <Box
          onClick={(e) => e.preventDefault()}
          sx={{
            display: "flex",
            gap: 1,
            overflowX: "auto",
            px: 1.5,
            pb: 1.5,
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {media.map((m, i) => (
            <Box
              key={i}
              component="img"
              src={m.url}
              alt=""
              sx={{
                width: 90,
                height: 90,
                borderRadius: 1.5,
                objectFit: "cover",
                flexShrink: 0,
                cursor: "pointer",
              }}
            />
          ))}
        </Box>
      )}

      {/* Footer: "Also rated" cross-link */}
      {isProductCentric ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1,
            borderTop: "1px solid #f3f4f6",
            bgcolor: "#f9fafb",
          }}
        >
          <StorefrontOutlinedIcon sx={{ fontSize: 14, color: "#046287" }} />
          <Typography variant="caption" color="text.secondary">
            Also rated:
          </Typography>
          <Typography
            variant="caption"
            fontWeight={500}
            color="text.primary"
            noWrap
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            {shop?.shopName || "the shop"}
            {shop?.isIdentityVerified && <VerifiedBadge size={12} />}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Rating
            value={review.shopRating || 0}
            precision={0.1}
            readOnly
            size="small"
            sx={{ fontSize: 11 }}
          />
        </Box>
      ) : review.productRatings?.length > 0 ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1,
            borderTop: "1px solid #f3f4f6",
            bgcolor: "#f9fafb",
          }}
        >
          <Inventory2OutlinedIcon sx={{ fontSize: 14, color: "#046287" }} />
          <Typography variant="caption" color="text.secondary">
            Also rated:
          </Typography>
          <Typography
            variant="caption"
            fontWeight={500}
            color="text.primary"
            noWrap
            sx={{ flex: 1 }}
          >
            {review.productRatings[0].productName}
          </Typography>
          <Rating
            value={review.productRatings[0].rating || 0}
            precision={0.1}
            readOnly
            size="small"
            sx={{ fontSize: 11 }}
          />
        </Box>
      ) : null}

      {/* Like row */}
      <Box
        sx={{
          display: "flex",
          px: 1.5,
          py: 1,
          bgcolor: "#f9fafb",
          borderTop: "1px solid #f3f4f6",
        }}
      >
        <LikeButton
          reviewId={review._id?.toString() || review.id?.toString()}
          likesCount={review.likesCount ?? 0}
          isLikedByUser={review.isLikedByUser ?? false}
        />
      </Box>
    </Box>
  );
};

export default FeedReviewCard;
